
import { useState } from 'react';
import { 
    Bot, Wrench, Brain, Workflow, Search, AlertTriangle, 
    Shield, FileText, Sparkles, Bell, Database, UserCog, 
    ChevronDown, ChevronUp, ExternalLink, Code2, Zap,
    Clock, DollarSign, Tag, GitBranch
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { capabilityColors } from '@/lib/capability-colors';
import type { TaskConfig, CapabilityMetadata, CapabilityType, CapabilitySemantics } from '../types';

interface CapabilityTabProps {
    config: TaskConfig;
    capabilities: CapabilityMetadata[];
    selectedCapability: CapabilityMetadata | null;
    onCapabilityChange: (key: string) => void;
    readOnly: boolean;
}

// Icons for each capability type
const typeIcons: Record<CapabilityType, typeof Bot> = {
    agent: Bot,
    tool: Wrench,
    model: Brain,
    workflow: Workflow,
    policy: Shield,
    prompt: FileText,
    role: UserCog,
    schema: Database,
    skill: Sparkles,
    subscription: Bell,
};

// Colors for each capability type - uses centralized capability colors
const typeColors: Record<CapabilityType, string> = {
    agent: `${capabilityColors.agent.bgLight} ${capabilityColors.agent.text} border-cyan-200`,
    tool: `${capabilityColors.tool.bgLight} ${capabilityColors.tool.text} border-violet-200`,
    model: `${capabilityColors.model.bgLight} ${capabilityColors.model.text} border-amber-200`,
    workflow: `${capabilityColors.workflow.bgLight} ${capabilityColors.workflow.text} border-indigo-200`,
    policy: `${capabilityColors.policy.bgLight} ${capabilityColors.policy.text} border-orange-200`,
    prompt: `${capabilityColors.prompt.bgLight} ${capabilityColors.prompt.text} border-rose-200`,
    role: `${capabilityColors.role.bgLight} ${capabilityColors.role.text} border-teal-200`,
    schema: `${capabilityColors.schema.bgLight} ${capabilityColors.schema.text} border-sky-200`,
    skill: `${capabilityColors.skill.bgLight} ${capabilityColors.skill.text} border-fuchsia-200`,
    subscription: `${capabilityColors.subscription.bgLight} ${capabilityColors.subscription.text} border-pink-200`,
};

// Semantics badges
const semanticsColors: Record<CapabilitySemantics, string> = {
    work: 'bg-blue-100 text-blue-700',
    declarative: 'bg-amber-100 text-amber-700',
    informational: 'bg-green-100 text-green-700',
};

export function CapabilityTab({
    config,
    capabilities,
    selectedCapability,
    onCapabilityChange,
    readOnly,
}: CapabilityTabProps) {
    const [search, setSearch] = useState('');
    const [showChangeWarning, setShowChangeWarning] = useState(false);
    const [detailsSectionOpen, setDetailsSectionOpen] = useState(true);
    const [capabilitiesSectionOpen, setCapabilitiesSectionOpen] = useState(true);
    const [typeSpecificSectionOpen, setTypeSpecificSectionOpen] = useState(true);

    // Filter capabilities by search
    const filteredCapabilities = capabilities.filter((cap) => {
        const searchLower = search.toLowerCase();
        return (
            cap.key.toLowerCase().includes(searchLower) ||
            cap.name.toLowerCase().includes(searchLower) ||
            cap.description?.toLowerCase().includes(searchLower) ||
            cap.tags?.some((t) => t.toLowerCase().includes(searchLower)) ||
            cap.aliases?.some((a) => a.toLowerCase().includes(searchLower))
        );
    });

    // Group by type
    const groupedCapabilities = filteredCapabilities.reduce((acc, cap) => {
        const type = cap.type;
        if (!acc[type]) acc[type] = [];
        acc[type].push(cap);
        return acc;
    }, {} as Record<string, CapabilityMetadata[]>);

    const handleCapabilitySelect = (key: string) => {
        if (config.capabilityKey && config.capabilityKey !== key) {
            setShowChangeWarning(true);
            setTimeout(() => {
                onCapabilityChange(key);
                setShowChangeWarning(false);
            }, 500);
        } else {
            onCapabilityChange(key);
        }
    };

    return (
        <div className="p-6 space-y-6">
            {/* Change Warning */}
            {showChangeWarning && (
                <Alert variant="destructive" className="border-amber-200 bg-amber-50">
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                    <AlertDescription className="text-amber-800">
                        Changing the capability will clear your input field mappings.
                    </AlertDescription>
                </Alert>
            )}

            {/* Selected Capability Details */}
            {selectedCapability && (
                <div className="space-y-4">
                    {/* Main Info Card */}
                    <Collapsible open={detailsSectionOpen} onOpenChange={setDetailsSectionOpen}>
                        <CollapsibleTrigger className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-slate-50 p-4 hover:bg-slate-100 transition-colors">
                            <div className="flex items-center gap-4">
                                <div className={cn(
                                    "flex h-12 w-12 items-center justify-center rounded-lg border",
                                    typeColors[selectedCapability.type]
                                )}>
                                    {(() => {
                                        const Icon = typeIcons[selectedCapability.type];
                                        return <Icon size={24} />;
                                    })()}
                                </div>
                                <div className="text-left">
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-semibold text-slate-900">
                                            {selectedCapability.name}
                                        </h3>
                                        <Badge variant="outline" className="text-xs">
                                            {selectedCapability.type}
                                        </Badge>
                                        <Badge variant="secondary" className="text-xs font-mono">
                                            v{selectedCapability.version}
                                        </Badge>
                                    </div>
                                    <code className="text-sm text-slate-500 font-mono">
                                        {selectedCapability.key}
                                    </code>
                                </div>
                            </div>
                            {detailsSectionOpen ? (
                                <ChevronUp size={20} className="text-slate-400" />
                            ) : (
                                <ChevronDown size={20} className="text-slate-400" />
                            )}
                        </CollapsibleTrigger>
                        
                        <CollapsibleContent className="mt-2 rounded-lg border border-slate-200 bg-white p-4 space-y-4">
                            {/* Base Fields */}
                            <div className="grid grid-cols-2 gap-4">
                                {/* Name */}
                                <div>
                                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Name</Label>
                                    <p className="font-medium text-slate-900">{selectedCapability.name}</p>
                                </div>
                                
                                {/* Version */}
                                <div>
                                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Version</Label>
                                    <p className="font-mono text-slate-900">{selectedCapability.version}</p>
                                </div>
                                
                                {/* Type */}
                                <div>
                                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Type</Label>
                                    <div className="flex items-center gap-2 mt-1">
                                        {(() => {
                                            const Icon = typeIcons[selectedCapability.type];
                                            return <Icon size={14} className={typeColors[selectedCapability.type].split(' ')[1]} />;
                                        })()}
                                        <span className="capitalize">{selectedCapability.type}</span>
                                    </div>
                                </div>
                                
                                {/* Execution Mode */}
                                <div>
                                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Execution Mode</Label>
                                    <div className="flex items-center gap-2 mt-1">
                                        <Zap size={14} className={cn(
                                            selectedCapability.executionMode === 'sync' && "text-green-500",
                                            selectedCapability.executionMode === 'job' && "text-orange-500",
                                            selectedCapability.executionMode === 'callback' && "text-purple-500"
                                        )} />
                                        <span className="capitalize">{selectedCapability.executionMode}</span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Description */}
                            {selectedCapability.description && (
                                <div>
                                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Description</Label>
                                    <p className="text-slate-700 mt-1">{selectedCapability.description}</p>
                                </div>
                            )}
                            
                            {/* Aliases */}
                            {selectedCapability.aliases && selectedCapability.aliases.length > 0 && (
                                <div>
                                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Aliases</Label>
                                    <div className="flex gap-1.5 flex-wrap mt-1">
                                        {selectedCapability.aliases.map((alias) => (
                                            <Badge key={alias} variant="outline" className="text-xs font-mono">
                                                {alias}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Tags */}
                            {selectedCapability.tags && selectedCapability.tags.length > 0 && (
                                <div>
                                    <Label className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1">
                                        <Tag size={12} /> Tags
                                    </Label>
                                    <div className="flex gap-1.5 flex-wrap mt-1">
                                        {selectedCapability.tags.map((tag) => (
                                            <Badge key={tag} variant="secondary" className="text-xs">
                                                {tag}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {/* Implementation Class */}
                            {selectedCapability.class && (
                                <div>
                                    <Label className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1">
                                        <Code2 size={12} /> Implementation Class
                                    </Label>
                                    <code className="text-sm font-mono text-slate-700 bg-slate-100 px-2 py-1 rounded mt-1 block">
                                        {selectedCapability.class}
                                    </code>
                                </div>
                            )}
                        </CollapsibleContent>
                    </Collapsible>

                    {/* Capabilities Definitions */}
                    {selectedCapability.capabilities && Object.keys(selectedCapability.capabilities).length > 0 && (
                        <Collapsible open={capabilitiesSectionOpen} onOpenChange={setCapabilitiesSectionOpen}>
                            <CollapsibleTrigger className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white p-3 hover:bg-slate-50 transition-colors">
                                <div className="flex items-center gap-2">
                                    <GitBranch size={16} className="text-slate-500" />
                                    <span className="font-medium text-slate-900">
                                        Capability Methods ({Object.keys(selectedCapability.capabilities).length})
                                    </span>
                                </div>
                                {capabilitiesSectionOpen ? (
                                    <ChevronUp size={16} className="text-slate-400" />
                                ) : (
                                    <ChevronDown size={16} className="text-slate-400" />
                                )}
                            </CollapsibleTrigger>
                            
                            <CollapsibleContent className="mt-2 space-y-2">
                                {Object.entries(selectedCapability.capabilities).map(([methodName, capDef]) => (
                                    <div key={methodName} className="rounded-lg border border-slate-200 bg-white p-3">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <code className="font-mono font-medium text-slate-900">
                                                    {methodName}()
                                                </code>
                                                <Badge className={cn("text-[10px]", semanticsColors[capDef.semantics])}>
                                                    {capDef.semantics}
                                                </Badge>
                                            </div>
                                        </div>
                                        {capDef.description && (
                                            <p className="text-sm text-slate-600">{capDef.description}</p>
                                        )}
                                        <div className="flex gap-4 mt-2 text-xs text-slate-500">
                                            {(capDef.input || capDef.inputSchema) && (
                                                <span className="flex items-center gap-1">
                                                    <span className="text-green-500">→</span>
                                                    Input: {capDef.inputSchema || 'inline schema'}
                                                </span>
                                            )}
                                            {(capDef.output || capDef.outputSchema) && (
                                                <span className="flex items-center gap-1">
                                                    <span className="text-blue-500">←</span>
                                                    Output: {capDef.outputSchema || 'inline schema'}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </CollapsibleContent>
                        </Collapsible>
                    )}

                    {/* Type-Specific Fields */}
                    {renderTypeSpecificFields(selectedCapability, typeSpecificSectionOpen, setTypeSpecificSectionOpen)}
                </div>
            )}

            {/* No capability selected placeholder */}
            {!selectedCapability && config.capabilityKey && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                        <div>
                            <h3 className="font-medium text-amber-900">
                                Unknown Capability
                            </h3>
                            <p className="text-sm text-amber-700 mt-1">
                                The capability <code className="font-mono">{config.capabilityKey}</code> is not found in the registry.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Search and Capability List (only if not read-only) */}
            {!readOnly && (
                <>
                    <div className="space-y-2">
                        <Label>Select Capability</Label>
                        <div className="relative">
                            <Search
                                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                size={16}
                            />
                            <Input
                                type="text"
                                placeholder="Search capabilities by name, key, tag, or alias..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-9"
                            />
                        </div>
                    </div>

                    {/* Capability Grid */}
                    <div className="space-y-4 max-h-[400px] overflow-y-auto">
                        {Object.entries(groupedCapabilities).map(([type, caps]) => (
                            <div key={type}>
                                <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                    {(() => {
                                        const Icon = typeIcons[type as CapabilityType];
                                        return <Icon size={12} />;
                                    })()}
                                    {type}s ({caps.length})
                                </h4>
                                <div className="grid grid-cols-2 gap-2">
                                    {caps.map((cap) => {
                                        const Icon = typeIcons[cap.type];
                                        const isSelected = config.capabilityKey === cap.key;

                                        return (
                                            <button
                                                key={cap.key}
                                                onClick={() => handleCapabilitySelect(cap.key)}
                                                className={cn(
                                                    "flex items-start gap-3 p-3 rounded-lg border text-left transition-all",
                                                    isSelected
                                                        ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                                                        : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                                                )}
                                            >
                                                <Icon
                                                    size={16}
                                                    className={cn(
                                                        "mt-0.5 shrink-0",
                                                        isSelected ? "text-blue-600" : "text-slate-400"
                                                    )}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <p className={cn(
                                                        "text-sm font-medium truncate",
                                                        isSelected ? "text-blue-900" : "text-slate-900"
                                                    )}>
                                                        {cap.name}
                                                    </p>
                                                    <p className="text-xs text-slate-500 font-mono truncate">
                                                        {cap.key}
                                                    </p>
                                                    {cap.description && (
                                                        <p className="text-xs text-slate-400 truncate mt-0.5">
                                                            {cap.description}
                                                        </p>
                                                    )}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}

                        {Object.keys(groupedCapabilities).length === 0 && (
                            <div className="text-center py-8 text-slate-500">
                                {search
                                    ? 'No capabilities match your search'
                                    : capabilities.length === 0
                                        ? 'No capabilities available'
                                        : 'No capabilities match your search'}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Read-only mode message */}
            {readOnly && !selectedCapability && !config.capabilityKey && (
                <div className="text-center py-8 text-slate-400 border border-dashed border-slate-200 rounded-lg">
                    No capability configured for this task.
                </div>
            )}
        </div>
    );
}

/**
 * Render type-specific fields based on capability type
 */
function renderTypeSpecificFields(
    cap: CapabilityMetadata,
    isOpen: boolean,
    setIsOpen: (open: boolean) => void
) {
    const hasTypeSpecificFields = getTypeSpecificFieldsCount(cap) > 0;
    
    if (!hasTypeSpecificFields) return null;

    return (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <CollapsibleTrigger className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white p-3 hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-2">
                    {(() => {
                        const Icon = typeIcons[cap.type];
                        return <Icon size={16} className="text-slate-500" />;
                    })()}
                    <span className="font-medium text-slate-900">
                        {cap.type.charAt(0).toUpperCase() + cap.type.slice(1)} Configuration
                    </span>
                </div>
                {isOpen ? (
                    <ChevronUp size={16} className="text-slate-400" />
                ) : (
                    <ChevronDown size={16} className="text-slate-400" />
                )}
            </CollapsibleTrigger>
            
            <CollapsibleContent className="mt-2 rounded-lg border border-slate-200 bg-white p-4 space-y-4">
                {cap.type === 'agent' && renderAgentFields(cap)}
                {cap.type === 'model' && renderModelFields(cap)}
                {cap.type === 'tool' && renderToolFields(cap)}
                {cap.type === 'workflow' && renderWorkflowFields(cap)}
                {cap.type === 'policy' && renderPolicyFields(cap)}
                {cap.type === 'skill' && renderSkillFields(cap)}
                {cap.type === 'role' && renderRoleFields(cap)}
                {cap.type === 'subscription' && renderSubscriptionFields(cap)}
            </CollapsibleContent>
        </Collapsible>
    );
}

function getTypeSpecificFieldsCount(cap: CapabilityMetadata): number {
    let count = 0;
    
    // Agent fields
    if (cap.prompt) count++;
    if (cap.model) count++;
    if (cap.tools?.length) count++;
    if (cap.skills?.length) count++;
    if (cap.roles?.length) count++;
    if (cap.preProcessorWorkflow) count++;
    if (cap.postProcessorWorkflow) count++;
    if (cap.config && Object.keys(cap.config).length) count++;
    
    // Model fields
    if (cap.provider) count++;
    if (cap.modelId) count++;
    if (cap.parameters) count++;
    if (cap.retry) count++;
    if (cap.backup) count++;
    if (cap.pricing) count++;
    
    // Workflow fields
    if (cap.adapter) count++;
    
    // Policy fields
    if (cap.opaPackage) count++;
    
    // Skill fields
    if (cap.priority !== undefined) count++;
    if (cap.conflictsWith?.length) count++;
    if (cap.systemInstruction) count++;
    
    // Role fields
    if (cap.extends) count++;
    if (cap.requiredSkills?.length) count++;
    if (cap.allowedModels?.length) count++;
    if (cap.policies?.length) count++;
    if (cap.constraints) count++;
    
    // Subscription fields
    if (cap.active !== undefined) count++;
    if (cap.source) count++;
    if (cap.target) count++;
    if (cap.inputMapping) count++;
    if (cap.filters?.length) count++;
    
    return count;
}

function renderAgentFields(cap: CapabilityMetadata) {
    return (
        <div className="grid grid-cols-2 gap-4 text-sm">
            {cap.prompt && (
                <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Prompt</Label>
                    <code className="text-slate-700 font-mono block mt-1">{cap.prompt}</code>
                </div>
            )}
            {cap.model && (
                <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Model</Label>
                    <code className="text-slate-700 font-mono block mt-1">{cap.model}</code>
                </div>
            )}
            {cap.tools && cap.tools.length > 0 && (
                <div className="col-span-2">
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Available Tools</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                        {cap.tools.map((tool) => (
                            <Badge key={tool} variant="outline" className="text-xs font-mono">
                                {tool}
                            </Badge>
                        ))}
                    </div>
                </div>
            )}
            {cap.skills && cap.skills.length > 0 && (
                <div className="col-span-2">
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Skills</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                        {cap.skills.map((skill) => (
                            <Badge key={skill} variant="secondary" className="text-xs">
                                {skill}
                            </Badge>
                        ))}
                    </div>
                </div>
            )}
            {cap.roles && cap.roles.length > 0 && (
                <div className="col-span-2">
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Roles</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                        {cap.roles.map((role) => (
                            <Badge key={role} className="text-xs bg-orange-100 text-orange-700">
                                {role}
                            </Badge>
                        ))}
                    </div>
                </div>
            )}
            {cap.preProcessorWorkflow && (
                <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Pre-Processor</Label>
                    <code className="text-slate-700 font-mono block mt-1">{cap.preProcessorWorkflow}</code>
                </div>
            )}
            {cap.postProcessorWorkflow && (
                <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Post-Processor</Label>
                    <code className="text-slate-700 font-mono block mt-1">{cap.postProcessorWorkflow}</code>
                </div>
            )}
            {cap.config && Object.keys(cap.config).length > 0 && (
                <div className="col-span-2">
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Config Overrides</Label>
                    <div className="bg-slate-50 rounded p-2 mt-1 font-mono text-xs">
                        {Object.entries(cap.config).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                                <span className="text-slate-600">{key}:</span>
                                <span className="text-slate-900">{JSON.stringify(value)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function renderModelFields(cap: CapabilityMetadata) {
    return (
        <div className="grid grid-cols-2 gap-4 text-sm">
            {cap.provider && (
                <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Provider</Label>
                    <p className="text-slate-700 mt-1 capitalize">{cap.provider}</p>
                </div>
            )}
            {cap.modelId && (
                <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Model ID</Label>
                    <code className="text-slate-700 font-mono block mt-1">{cap.modelId}</code>
                </div>
            )}
            {cap.parameters && (
                <div className="col-span-2">
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Parameters</Label>
                    <div className="grid grid-cols-3 gap-2 mt-1">
                        {cap.parameters.timeout && (
                            <div className="flex items-center gap-1.5 text-xs">
                                <Clock size={12} className="text-slate-400" />
                                <span>Timeout: {cap.parameters.timeout}s</span>
                            </div>
                        )}
                        {cap.parameters.temperature !== undefined && (
                            <div className="text-xs">Temperature: {cap.parameters.temperature}</div>
                        )}
                        {cap.parameters.topP !== undefined && (
                            <div className="text-xs">Top P: {cap.parameters.topP}</div>
                        )}
                    </div>
                </div>
            )}
            {cap.backup && (
                <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Fallback Model</Label>
                    <code className="text-slate-700 font-mono block mt-1">{cap.backup}</code>
                </div>
            )}
            {cap.pricing && (
                <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-1">
                        <DollarSign size={12} /> Pricing
                    </Label>
                    <div className="text-xs mt-1 space-y-0.5">
                        <div>Input: ${cap.pricing.inputPerMillion}/M tokens</div>
                        <div>Output: ${cap.pricing.outputPerMillion}/M tokens</div>
                    </div>
                </div>
            )}
        </div>
    );
}

function renderToolFields(cap: CapabilityMetadata) {
    return (
        <div className="text-sm">
            {cap.config && Object.keys(cap.config).length > 0 && (
                <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Configuration</Label>
                    <div className="bg-slate-50 rounded p-2 mt-1 font-mono text-xs">
                        {Object.entries(cap.config).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                                <span className="text-slate-600">{key}:</span>
                                <span className="text-slate-900">{JSON.stringify(value)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function renderWorkflowFields(cap: CapabilityMetadata) {
    return (
        <div className="text-sm">
            {cap.adapter && (
                <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Execution Adapter</Label>
                    <code className="text-slate-700 font-mono block mt-1">{cap.adapter}</code>
                </div>
            )}
        </div>
    );
}

function renderPolicyFields(cap: CapabilityMetadata) {
    return (
        <div className="text-sm">
            {cap.opaPackage && (
                <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">OPA Package</Label>
                    <code className="text-slate-700 font-mono block mt-1">{cap.opaPackage}</code>
                </div>
            )}
        </div>
    );
}

function renderSkillFields(cap: CapabilityMetadata) {
    return (
        <div className="grid grid-cols-2 gap-4 text-sm">
            {cap.priority !== undefined && (
                <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Priority</Label>
                    <p className="text-slate-700 mt-1">{cap.priority}</p>
                </div>
            )}
            {cap.conflictsWith && cap.conflictsWith.length > 0 && (
                <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Conflicts With</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                        {cap.conflictsWith.map((conflict) => (
                            <Badge key={conflict} variant="destructive" className="text-xs">
                                {conflict}
                            </Badge>
                        ))}
                    </div>
                </div>
            )}
            {cap.systemInstruction && (
                <div className="col-span-2">
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">System Instruction</Label>
                    <p className="text-slate-700 mt-1 text-sm bg-slate-50 rounded p-2">
                        {cap.systemInstruction}
                    </p>
                </div>
            )}
        </div>
    );
}

function renderRoleFields(cap: CapabilityMetadata) {
    return (
        <div className="grid grid-cols-2 gap-4 text-sm">
            {cap.extends && (
                <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Extends</Label>
                    <code className="text-slate-700 font-mono block mt-1">{cap.extends}</code>
                </div>
            )}
            {cap.requiredSkills && cap.requiredSkills.length > 0 && (
                <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Required Skills</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                        {cap.requiredSkills.map((skill) => (
                            <Badge key={skill} variant="secondary" className="text-xs">
                                {skill}
                            </Badge>
                        ))}
                    </div>
                </div>
            )}
            {cap.allowedModels && cap.allowedModels.length > 0 && (
                <div className="col-span-2">
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Allowed Models</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                        {cap.allowedModels.map((model) => (
                            <Badge key={model} variant="outline" className="text-xs font-mono">
                                {model}
                            </Badge>
                        ))}
                    </div>
                </div>
            )}
            {cap.constraints && (
                <div className="col-span-2">
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Constraints</Label>
                    <div className="bg-slate-50 rounded p-2 mt-1 text-xs space-y-1">
                        {cap.constraints.maxTokens && <div>Max Tokens: {cap.constraints.maxTokens}</div>}
                        {cap.constraints.maxConcurrent && <div>Max Concurrent: {cap.constraints.maxConcurrent}</div>}
                        {cap.constraints.temperatureRange && (
                            <div>Temperature: {cap.constraints.temperatureRange.min} - {cap.constraints.temperatureRange.max}</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function renderSubscriptionFields(cap: CapabilityMetadata) {
    return (
        <div className="grid grid-cols-2 gap-4 text-sm">
            {cap.active !== undefined && (
                <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Status</Label>
                    <Badge className={cn("mt-1", cap.active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700")}>
                        {cap.active ? 'Active' : 'Inactive'}
                    </Badge>
                </div>
            )}
            {cap.source && (
                <div>
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Source</Label>
                    <div className="mt-1">
                        <Badge variant="outline" className="text-xs">{cap.source.type}</Badge>
                        <code className="text-xs font-mono ml-2">{cap.source.identifier}</code>
                    </div>
                </div>
            )}
            {cap.target && (
                <div className="col-span-2">
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Target</Label>
                    <code className="text-slate-700 font-mono block mt-1">
                        {cap.target.capability}.{cap.target.method}()
                    </code>
                </div>
            )}
            {cap.filters && cap.filters.length > 0 && (
                <div className="col-span-2">
                    <Label className="text-xs text-slate-500 uppercase tracking-wider">Filters ({cap.filters.length})</Label>
                    <div className="bg-slate-50 rounded p-2 mt-1 text-xs font-mono space-y-1">
                        {cap.filters.map((filter, i) => (
                            <div key={i}>
                                {filter.path} {filter.operator} {JSON.stringify(filter.value)}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
